import React, { useState, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { utils, writeFile } from "xlsx";

// --- Utility Functions ---
const generateSafeId = (label, index) => {
  if (!label) return `field_${index}`;
  return label.toLowerCase().replace(/[^a-z0-9]/g, "_") + `_${index}`;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const secureFetcher = (url) =>
  fetch(url, { cache: "no-store" }).then((res) => res.json());

// Deep Condition Check (Array & String Both Supported)
const isFieldVisible = (field, formValues, formBuilderConfig) => {
  let currentField = field;
  let depth = 0;
  const MAX_DEPTH = 10;

  while (
    currentField &&
    currentField.condition_id &&
    currentField.condition_value &&
    depth < MAX_DEPTH
  ) {
    const expectedVal = String(currentField.condition_value).trim();
    const actualValRaw = formValues[currentField.condition_id];

    let conditionMet = false;

    if (Array.isArray(actualValRaw)) {
      conditionMet = actualValRaw.includes(expectedVal);
    } else {
      const actualValStr =
        actualValRaw !== undefined ? String(actualValRaw).trim() : "";
      conditionMet = actualValStr === expectedVal;
    }

    if (!conditionMet) {
      return false;
    }

    const parentField = formBuilderConfig.find((f, i) => {
      const fId = f.unique_id || generateSafeId(f.label, i);
      return fId === currentField.condition_id;
    });

    if (!parentField || parentField === currentField) break;
    currentField = parentField;
    depth++;
  }

  return true;
};

// Check Multi-Select Helper
const checkIsMulti = (field) => {
  if (!field || !field.is_multiselect) return false;
  const raw = field.is_multiselect;
  if (raw === true) return true;
  if (Array.isArray(raw))
    return raw.some((r) => String(r).toLowerCase().trim() === "yes");
  return String(raw).toLowerCase().trim() === "yes";
};

// ============================================================================
// SUB-COMPONENT 1: Standard Range Slider
// ============================================================================
const RangeSliderField = ({ field, fieldId, value, onChange, dynamicUnit }) => {
  const min = Number(field.min_value) || 0;
  const max = Number(field.max_value) || 100;
  const step = Number(field.step_up) || 1;
  const val = value !== undefined ? Number(value) : min;

  const clampedVal = Math.min(Math.max(val, min), max);
  const percent = max > min ? ((clampedVal - min) / (max - min)) * 100 : 0;

  let unitLabel = field.unit_label || "";
  if (dynamicUnit) {
    unitLabel = dynamicUnit;
  } else if (!unitLabel) {
    const lowerLabel = field.label.toLowerCase();
    if (lowerLabel.includes("vcpu")) unitLabel = "vCPUs";
    else if (lowerLabel.includes("gib") || lowerLabel.includes("memory"))
      unitLabel = "GiB";
    else if (lowerLabel.includes("gb") || lowerLabel.includes("size"))
      unitLabel = "GB";
    else if (lowerLabel.includes("tb")) unitLabel = "TB";
  }

  const thumbOffset = 10 - percent * 0.2;

  return (
    <div className="col-12 mb-4">
      <label className="sky-field-label">{field.label}</label>

      <div className="sky-slider-container align-items-end">
        <div className="sky-slider-wrapper">
          <div
            className="sky-slider-tooltip"
            style={{ left: `calc(${percent}% + ${thumbOffset}px)` }}
          >
            {val} {unitLabel}
          </div>

          <input
            type="range"
            className="sky-range-slider"
            min={min}
            max={max}
            step={step}
            value={val}
            onChange={(e) => onChange(fieldId, Number(e.target.value))}
            style={{
              background: `linear-gradient(to right, #b0d7e7 0%, #3db4e5 ${percent}%, var(--sky-track-bg) ${percent}%, var(--sky-track-bg) 100%)`,
            }}
          />
        </div>

        <input
          type="number"
          className="sky-number-box"
          min={min}
          max={max}
          step={step}
          value={val}
          onChange={(e) => onChange(fieldId, Number(e.target.value))}
        />
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENT 2: Matrix/Tiered Slider
// ============================================================================
const MatrixSliderField = ({ field, fieldId, value, onChange }) => {
  const tiers = useMemo(() => {
    try {
      return JSON.parse(field.pricing_tiers || "[]");
    } catch (e) {
      return [];
    }
  }, [field.pricing_tiers]);

  if (tiers.length === 0) return null;

  const maxIndex = tiers.length - 1;
  const currentIndex = Math.min(Math.max(Number(value) || 0, 0), maxIndex);
  const currentTier = tiers[currentIndex];

  const percent = maxIndex > 0 ? (currentIndex / maxIndex) * 100 : 0;
  const thumbOffset = 10 - percent * 0.2;

  const handleSliderChange = (e) => onChange(fieldId, Number(e.target.value));

  const handleSnapInput = (inputValue, type) => {
    const val = Number(inputValue);
    let closestIndex = 0;
    let minDiff = Infinity;
    tiers.forEach((t, i) => {
      const diff = Math.abs(t[type] - val);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    });
    onChange(fieldId, closestIndex);
  };

  return (
    <div className="col-12 mb-4 sky-matrix-group">
      <div className="mb-4">
        <label className="sky-field-label">Number of vCPUs</label>
        <div className="sky-slider-container align-items-end">
          <div className="sky-slider-wrapper">
            <div
              className="sky-slider-tooltip"
              style={{ left: `calc(${percent}% + ${thumbOffset}px)` }}
            >
              {currentTier.vcpu} vCPUs
            </div>
            <input
              type="range"
              className="sky-range-slider"
              min={0}
              max={maxIndex}
              step={1}
              value={currentIndex}
              onChange={handleSliderChange}
              style={{
                background: `linear-gradient(to right, #b0d7e7 0%, #3db4e5 ${percent}%, var(--sky-track-bg) ${percent}%, var(--sky-track-bg) 100%)`,
              }}
            />
          </div>
          <input
            type="number"
            className="sky-number-box"
            value={currentTier.vcpu}
            onChange={(e) => handleSnapInput(e.target.value, "vcpu")}
          />
        </div>
      </div>
      <div className="mb-2">
        <label className="sky-field-label">Amount of Memory</label>
        <div className="sky-slider-container align-items-end">
          <div className="sky-slider-wrapper">
            <div
              className="sky-slider-tooltip"
              style={{ left: `calc(${percent}% + ${thumbOffset}px)` }}
            >
              {currentTier.ram} GiB
            </div>
            <input
              type="range"
              className="sky-range-slider"
              min={0}
              max={maxIndex}
              step={1}
              value={currentIndex}
              onChange={handleSliderChange}
              style={{
                background: `linear-gradient(to right, #b0d7e7 0%, #3db4e5 ${percent}%, var(--sky-track-bg) ${percent}%, var(--sky-track-bg) 100%)`,
              }}
            />
          </div>
          <input
            type="number"
            className="sky-number-box"
            value={currentTier.ram}
            onChange={(e) => handleSnapInput(e.target.value, "ram")}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENT 3: Custom Multi-Select Dropdown with Tags/Chips
// ============================================================================
const MultiSelectDropdownField = ({ field, fieldId, value = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckboxChange = (optName) => {
    const currentSelection = Array.isArray(value) ? value : [];
    let newSelection = [...currentSelection];

    if (newSelection.includes(optName)) {
      newSelection = newSelection.filter((v) => v !== optName);
    } else {
      newSelection.push(optName);
    }
    onChange(fieldId, newSelection);
  };

  const handleRemoveTag = (e, optName) => {
    e.stopPropagation();
    const currentSelection = Array.isArray(value) ? value : [];
    const newSelection = currentSelection.filter((v) => v !== optName);
    onChange(fieldId, newSelection);
  };

  // Find all selected options that have a description
  const selectedOptionsWithDescription = field.options_list?.filter(
    (opt) =>
      value.includes(opt.option_name) &&
      opt.option_description &&
      opt.option_description.trim() !== "",
  );

  return (
    <div className="col-12 mb-4" key={fieldId} ref={dropdownRef}>
      <label className="sky-field-label">{field.label}</label>
      <div style={{ position: "relative" }}>
        <div
          className="sky-select-input"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            userSelect: "none",
            background: "#fff",
            height: "auto",
            minHeight: "45px",
            padding: "6px 12px",
          }}
        >
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: "6px", flex: 1 }}
          >
            {!Array.isArray(value) || value.length === 0 ? (
              <span
                style={{ color: "#777", padding: "4px 0", fontSize: "14px" }}
              >
                Select options...
              </span>
            ) : (
              value.map((val) => (
                <span
                  key={val}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: "#e0f3fa",
                    border: "1px solid #3db4e5",
                    color: "#333",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "13px",
                    lineHeight: "1",
                  }}
                >
                  {val}
                  <span
                    onClick={(e) => handleRemoveTag(e, val)}
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: "6px",
                      cursor: "pointer",
                      fontSize: "16px",
                      color: "#3db4e5",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.color = "#d9534f")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.color = "#3db4e5")
                    }
                  >
                    &times;
                  </span>
                </span>
              ))
            )}
          </div>

          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#666"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              marginLeft: "8px",
              flexShrink: 0,
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        {isOpen && (
          <div
            className="sky-multi-dropdown-menu"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 99,
              background: "#fff",
              border: "1px solid #dde3e8",
              borderRadius: "6px",
              marginTop: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              maxHeight: "250px",
              overflowY: "auto",
              padding: "8px 0",
            }}
          >
            {field.options_list?.map((opt, i) => {
              const isChecked =
                Array.isArray(value) && value.includes(opt.option_name);
              return (
                <label
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#333",
                    margin: 0,
                    background: isChecked ? "#f0f8ff" : "transparent",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = isChecked
                      ? "#e0f3fa"
                      : "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = isChecked
                      ? "#f0f8ff"
                      : "transparent")
                  }
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckboxChange(opt.option_name)}
                    style={{
                      accentColor: "#3db4e5",
                      width: "16px",
                      height: "16px",
                      margin: 0,
                      cursor: "pointer",
                    }}
                  />
                  {opt.option_name}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Render descriptions for selected multi-options if they exist */}
      {selectedOptionsWithDescription &&
        selectedOptionsWithDescription.length > 0 && (
          <div
            style={{
              marginTop: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {selectedOptionsWithDescription.map((opt, idx) => (
              <div
                key={idx}
                style={{ fontSize: "12px", color: "#666", lineHeight: "1.4" }}
              >
                <span style={{ fontWeight: "600", color: "#444" }}>
                  {opt.option_name}:
                </span>{" "}
                {opt.option_description}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

// ============================================================================
// COMPONENT 1: Top Section (Sidebar + Form)
// ============================================================================
const CalculatorBuilder = ({
  servicesData,
  cart,
  setCart,
  activeService,
  setActiveService,
  preSelectedItems,
}) => {
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (!activeService?.acf?.form_builder) return;

    const existingItem = cart.find(
      (item) => item.serviceId === activeService.id,
    );

    if (existingItem && existingItem.configuration) {
      const sanitizedConfig = { ...existingItem.configuration };
      activeService.acf.form_builder.forEach((field, index) => {
        const fieldId = field.unique_id || generateSafeId(field.label, index);
        if (checkIsMulti(field)) {
          if (
            typeof sanitizedConfig[fieldId] === "string" &&
            sanitizedConfig[fieldId] !== ""
          ) {
            sanitizedConfig[fieldId] = [sanitizedConfig[fieldId]];
          } else if (!Array.isArray(sanitizedConfig[fieldId])) {
            sanitizedConfig[fieldId] = [];
          }
        }
      });
      setFormValues(sanitizedConfig);
    } else {
      const initialValues = {};
      activeService.acf.form_builder.forEach((field, index) => {
        const fieldId = field.unique_id || generateSafeId(field.label, index);

        if (field.acf_fc_layout === "dropdown_selection") {
          if (checkIsMulti(field)) {
            initialValues[fieldId] = field.options_list?.[0]
              ? [field.options_list[0].option_name]
              : [];
          } else {
            initialValues[fieldId] = field.options_list?.[0]?.option_name || "";
          }
        } else if (field.acf_fc_layout === "slider_range") {
          initialValues[fieldId] = Number(field.min_value || 0);
        } else if (field.acf_fc_layout === "compute_engine") {
          initialValues[fieldId] = 0;
        }
      });
      setFormValues(initialValues);
    }
  }, [activeService?.id]);

  const handleInputChange = (fieldId, value) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const currentTotal = useMemo(() => {
    if (!activeService?.acf?.form_builder) return 0;

    let total = 0;
    let previousSliderValue = 1;

    activeService.acf.form_builder.forEach((field, index) => {
      const fieldId = field.unique_id || generateSafeId(field.label, index);
      const selectedValue = formValues[fieldId];

      if (!isFieldVisible(field, formValues, activeService.acf.form_builder)) {
        return;
      }

      if (field.acf_fc_layout === "slider_range") {
        const numericValue = Number(selectedValue) || 0;
        const unitPrice = Number(field.per_unit_price) || 0;
        total += numericValue * unitPrice;
        previousSliderValue = numericValue;
      } else if (field.acf_fc_layout === "dropdown_selection") {
        if (Array.isArray(selectedValue)) {
          selectedValue.forEach((val) => {
            const selectedOption = field.options_list?.find(
              (opt) => opt.option_name === val,
            );
            const optionPrice = Number(selectedOption?.option_price || 0);
            if (field.label.toLowerCase().includes("unit")) {
              total += previousSliderValue * optionPrice;
            } else {
              total += optionPrice;
            }
          });
        } else {
          const selectedOption = field.options_list?.find(
            (opt) => opt.option_name === selectedValue,
          );
          const optionPrice = Number(selectedOption?.option_price || 0);
          if (field.label.toLowerCase().includes("unit")) {
            total += previousSliderValue * optionPrice;
          } else {
            total += optionPrice;
          }
        }
      } else if (field.acf_fc_layout === "compute_engine") {
        try {
          const tiers = JSON.parse(field.pricing_tiers || "[]");
          const idx = Number(selectedValue) || 0;
          if (tiers[idx]) {
            total += Number(tiers[idx].price) || 0;
          }
        } catch (e) {}
      }
    });

    return total;
  }, [formValues, activeService]);

  const isItemInCart = cart.some(
    (item) => item.serviceId === activeService?.id,
  );

  const handleAddToList = () => {
    if (!activeService) return;
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (item) => item.serviceId === activeService.id,
      );

      if (existingItemIndex >= 0) {
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          configuration: formValues,
          price: currentTotal,
        };
        return updatedCart;
      } else {
        const newItem = {
          id: Date.now().toString(),
          serviceId: activeService.id,
          serviceName: activeService.title.rendered,
          configuration: formValues,
          price: currentTotal,
        };
        return [...prevCart, newItem];
      }
    });
  };

  if (!servicesData || servicesData.length === 0) return null;

  return (
    <div className="row">
      <div className="col-md-4 col-lg-3 sky-sidebar-col">
        {servicesData.map((service) => {
          const isPreSelected = preSelectedItems.some(
            (item) => item.id === service.id,
          );
          const isActive = activeService?.id === service.id;

          return (
            <button
              key={service.id}
              className={`sky-nav-btn ${isActive ? "active" : ""} ${isPreSelected ? "pre-selected-service" : ""}`}
              onClick={() => setActiveService(service)}
            >
              <span
                dangerouslySetInnerHTML={{ __html: service.title.rendered }}
              />
            </button>
          );
        })}
      </div>

      <div className="col-md-8 col-lg-9 sky-form-col">
        {activeService && (
          <>
            <h2
              className="sky-form-title"
              dangerouslySetInnerHTML={{ __html: activeService.title.rendered }}
            />

            <div className="row">
              {activeService.acf?.form_builder?.map((field, index) => {
                const fieldId =
                  field.unique_id || generateSafeId(field.label, index);

                if (
                  !isFieldVisible(
                    field,
                    formValues,
                    activeService.acf.form_builder,
                  )
                ) {
                  return null;
                }

                if (field.acf_fc_layout === "slider_range") {
                  let unitValue = null;
                  const unitFieldIndex =
                    activeService.acf.form_builder.findIndex(
                      (f) =>
                        f.acf_fc_layout === "dropdown_selection" &&
                        f.label &&
                        f.label.toLowerCase().includes("unit") &&
                        isFieldVisible(
                          f,
                          formValues,
                          activeService.acf.form_builder,
                        ),
                    );

                  if (unitFieldIndex !== -1) {
                    const unitField =
                      activeService.acf.form_builder[unitFieldIndex];
                    const unitFieldId =
                      unitField.unique_id ||
                      generateSafeId(unitField.label, unitFieldIndex);
                    unitValue = formValues[unitFieldId];
                  }

                  return (
                    <RangeSliderField
                      key={fieldId}
                      field={field}
                      fieldId={fieldId}
                      value={formValues[fieldId]}
                      onChange={handleInputChange}
                      dynamicUnit={unitValue}
                    />
                  );
                }

                if (field.acf_fc_layout === "compute_engine") {
                  return (
                    <MatrixSliderField
                      key={fieldId}
                      field={field}
                      fieldId={fieldId}
                      value={formValues[fieldId]}
                      onChange={handleInputChange}
                    />
                  );
                }

                if (field.acf_fc_layout === "dropdown_selection") {
                  if (checkIsMulti(field)) {
                    let currentSelection = formValues[fieldId];
                    if (
                      typeof currentSelection === "string" &&
                      currentSelection !== ""
                    ) {
                      currentSelection = [currentSelection];
                    } else if (!Array.isArray(currentSelection)) {
                      currentSelection = [];
                    }

                    return (
                      <MultiSelectDropdownField
                        key={fieldId}
                        field={field}
                        fieldId={fieldId}
                        value={currentSelection}
                        onChange={handleInputChange}
                      />
                    );
                  }

                  // Normal Single Select UI with Description Logic
                  const currentSelectedVal = formValues[fieldId] || "";
                  const selectedOptionObj = field.options_list?.find(
                    (opt) => opt.option_name === currentSelectedVal,
                  );
                  const hasDescription =
                    selectedOptionObj?.option_description &&
                    selectedOptionObj.option_description.trim() !== "";

                  return (
                    <div className="col-12 mb-4" key={fieldId}>
                      <label className="sky-field-label">{field.label}</label>
                      <select
                        className="sky-select-input"
                        value={currentSelectedVal}
                        onChange={(e) =>
                          handleInputChange(fieldId, e.target.value)
                        }
                      >
                        {field.options_list?.map((opt, i) => (
                          <option key={i} value={opt.option_name}>
                            {opt.option_name}
                          </option>
                        ))}
                      </select>

                      {/* Render option description if it exists */}
                      {hasDescription && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: "6px",
                            lineHeight: "1.4",
                          }}
                        >
                          {selectedOptionObj.option_description}
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </div>

            <div className="sky-action-bar">
              <button className="sky-btn-add" onClick={handleAddToList}>
                {isItemInCart ? "Update List" : "Add to List"}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
              </button>
              <span className="sky-price-text">
                {formatCurrency(currentTotal)} / month
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT 2: Summary Area (PDF and Excel Logic untouched)
// ============================================================================
const PriceSummary = ({ cart, setCart, servicesData, setActiveService }) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

  const grandTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price, 0),
    [cart],
  );

  const handleRemoveItem = (idToRemove) => {
    setCart(cart.filter((item) => item.id !== idToRemove));
    setOpenMenuId(null);
  };

  const handleEditItem = (serviceId) => {
    const serviceToEdit = servicesData.find((s) => s.id === serviceId);
    if (serviceToEdit) {
      setActiveService(serviceToEdit);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setOpenMenuId(null);
  };

  const handleDownloadPDF = async () => {
    const logoUrl = "assets/img/doc-logo.png";
    const logoImg = new Image();
    logoImg.src = logoUrl;
    logoImg.crossOrigin = "Anonymous";

    logoImg.onload = () => {
      const doc = new jsPDF();
      doc.addImage(logoImg, "PNG", 14, 10, 20, 20);
      doc.setFontSize(16);
      doc.setTextColor(50, 50, 50);
      doc.text("Cloud Infrastructure Estimate", 14, 36);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const date = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      doc.text(`Date Generated: ${date}`, 14, 43);
      doc.text(`Generated by: Sky47 Pricing Calculator`, 14, 48);

      const tableData = [];

      cart.forEach((item) => {
        const originalService = servicesData.find(
          (s) => s.id === item.serviceId,
        );
        let configText = "";

        if (originalService && item.configuration) {
          originalService.acf.form_builder.forEach((field, index) => {
            const fieldId =
              field.unique_id || generateSafeId(field.label, index);
            const val = item.configuration[fieldId];

            if (
              !isFieldVisible(
                field,
                item.configuration,
                originalService.acf.form_builder,
              )
            ) {
              return;
            }

            if (field.acf_fc_layout === "compute_engine") {
              try {
                const tiers = JSON.parse(field.pricing_tiers || "[]");
                const idx = Number(val) || 0;
                if (tiers[idx]) {
                  configText += `• vCPUs: ${tiers[idx].vcpu}\n`;
                  configText += `• Memory: ${tiers[idx].ram} GiB\n`;
                }
              } catch (e) {}
            } else if (val !== undefined && val !== "") {
              configText += `• ${field.label}: ${val}\n`;
            }
          });
        }

        tableData.push([
          stripHtml(item.serviceName),
          configText.trim(),
          formatCurrency(item.price),
        ]);
      });

      let tableEndY = 60;
      autoTable(doc, {
        startY: 58,
        head: [["Service Name", "Configuration Details", "Subtotal"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [61, 180, 229], textColor: 255 },
        styles: { cellPadding: 6, fontSize: 10, valign: "middle" },
        columnStyles: {
          0: { cellWidth: 45, fontStyle: "bold", textColor: [50, 50, 50] },
          1: { cellWidth: "auto", textColor: [80, 80, 80] },
          2: {
            cellWidth: 35,
            halign: "right",
            fontStyle: "bold",
            textColor: [50, 50, 50],
          },
        },
        didDrawPage: function (data) {
          tableEndY = data.cursor.y;
        },
      });

      let finalYPosition = tableEndY + 15;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Estimated Grand Total: ${formatCurrency(grandTotal)} / month`,
        14,
        finalYPosition,
      );

      finalYPosition += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "Disclaimer: This is an estimated cost. Actual charges may vary based on usage and final agreement.",
        14,
        finalYPosition,
      );

      doc.save("Sky47-Cloud-Estimate.pdf");
    };

    logoImg.onerror = () => {
      console.error(
        "PDF Logo load nahi ho saka. Image path ya CORS check karein.",
      );
      alert("Error generating PDF: Logo could not be loaded.");
    };
  };

  const handleDownloadExcel = () => {
    const sheetData = [
      ["Sky47 Cloud Infrastructure"],
      ["Pricing Estimate Document"],
      [
        "Date Generated:",
        new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      ],
      [],
      ["Service Name", "Configuration Details", "Subtotal ($)"],
    ];

    cart.forEach((item) => {
      const originalService = servicesData.find((s) => s.id === item.serviceId);
      let configDetails = "";

      if (originalService && item.configuration) {
        originalService.acf.form_builder.forEach((field, index) => {
          const fieldId = field.unique_id || generateSafeId(field.label, index);
          const val = item.configuration[fieldId];

          if (
            !isFieldVisible(
              field,
              item.configuration,
              originalService.acf.form_builder,
            )
          ) {
            return;
          }

          if (field.acf_fc_layout === "compute_engine") {
            try {
              const tiers = JSON.parse(field.pricing_tiers || "[]");
              const idx = Number(val) || 0;
              if (tiers[idx]) {
                configDetails += `• vCPUs: ${tiers[idx].vcpu}\n`;
                configDetails += `• Memory: ${tiers[idx].ram} GiB\n`;
              }
            } catch (e) {}
          } else if (val !== undefined && val !== "") {
            configDetails += `• ${field.label}: ${val}\n`;
          }
        });
      }
      sheetData.push([
        stripHtml(item.serviceName),
        configDetails.trim(),
        Number(item.price),
      ]);
    });

    sheetData.push([]);
    sheetData.push(["", "ESTIMATED GRAND TOTAL / mo:", Number(grandTotal)]);

    const ws = utils.aoa_to_sheet(sheetData);
    ws["!cols"] = [{ wch: 35 }, { wch: 65 }, { wch: 25 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sky47 Estimate");
    writeFile(wb, "Sky47-Cloud-Estimate.xlsx");
  };

  if (cart.length === 0) return null;

  return (
    <div className="sky-summary-box">
      <div className="sky-summary-header">Price Summary</div>

      <div className="sky-summary-body">
        <div className="sky-summary-title">Cost details</div>
        <div className="row g-3">
          {cart.map((item) => (
            <div className="col-md-6 col-lg-4" key={item.id}>
              <div className="sky-cart-card">
                <button
                  className="sky-kebab-btn"
                  onClick={() =>
                    setOpenMenuId(openMenuId === item.id ? null : item.id)
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="12" cy="5" r="1"></circle>
                    <circle cx="12" cy="19" r="1"></circle>
                  </svg>
                </button>

                {openMenuId === item.id && (
                  <div className="sky-dropdown-menu">
                    <button
                      className="sky-dropdown-item delete-text"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      Delete Item
                    </button>
                  </div>
                )}

                <div className="sky-cart-row pt-2 align-items-start">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="sky-edit-icon-btn"
                      onClick={() => handleEditItem(item.serviceId)}
                      title="Edit this configuration"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <span
                      className="sky-cart-name m-0"
                      dangerouslySetInnerHTML={{ __html: item.serviceName }}
                    />
                  </div>
                  <span className="sky-cart-price">
                    {formatCurrency(item.price)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sky-summary-footer">
        <div>
          <div className="sky-total-label">Estimated Cost</div>
          <div className="sky-total-amount">
            {formatCurrency(grandTotal)} / month
          </div>
        </div>

        <div className="position-relative d-flex gap-3">
          <button
            className="sky-btn-primary"
            onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
            title="Download Estimate"
          >
            Download
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginLeft: "6px" }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>

          {isDownloadMenuOpen && (
            <div
              className="sky-dropdown-menu"
              style={{
                display: "block",
                bottom: "100%",
                right: "0",
                top: "auto",
                marginBottom: "8px",
                minWidth: "180px",
              }}
            >
              <button
                className="sky-dropdown-item"
                onClick={() => {
                  handleDownloadPDF();
                  setIsDownloadMenuOpen(false);
                }}
              >
                Download as PDF
              </button>
              <button
                className="sky-dropdown-item"
                onClick={() => {
                  handleDownloadExcel();
                  setIsDownloadMenuOpen(false);
                }}
              >
                Download as Excel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN WRAPPER
// ============================================================================
export default function CloudPricingCalculator({ preSelectedItems = [] }) {
  const {
    data: rawData,
    error,
    isLoading,
  } = useSWR(
    "https://47.activ8.digital/wp-json/wp/v2/cloudservice?per_page=100&_embed",
    secureFetcher,
  );

  const [activeService, setActiveService] = useState(null);

  const sortedServicesData = useMemo(() => {
    if (!rawData) return [];
    const isSelected = (id) => preSelectedItems.some((item) => item.id === id);

    return [...rawData].sort((a, b) => {
      const aSelected = isSelected(a.id);
      const bSelected = isSelected(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      const orderA = a.acf?.price_calculator_order?.service_order || 0;
      const orderB = b.acf?.price_calculator_order?.service_order || 0;
      return orderA - orderB;
    });
  }, [rawData, preSelectedItems]);

  useEffect(() => {
    if (sortedServicesData.length > 0 && !activeService)
      setActiveService(sortedServicesData[0]);
  }, [sortedServicesData, activeService]);

  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("sky47_calc_cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("sky47_calc_cart", JSON.stringify(cart));
  }, [cart]);

  if (error)
    return (
      <div className="alert alert-danger text-center mt-5">
        Failed to load configuration.
      </div>
    );
  if (isLoading)
    return (
      <div className="text-center p-5 mt-5">
        <div
          className="spinner-border"
          style={{ color: "#3db4e5" }}
          role="status"
        ></div>
      </div>
    );

  return (
    <div className="container-custom">
      <CalculatorBuilder
        servicesData={sortedServicesData}
        cart={cart}
        setCart={setCart}
        activeService={activeService}
        setActiveService={setActiveService}
        preSelectedItems={preSelectedItems}
      />
      <PriceSummary
        cart={cart}
        setCart={setCart}
        servicesData={sortedServicesData}
        setActiveService={setActiveService}
      />
    </div>
  );
}
